import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { arDisplayStatus } from "@/lib/assetStatus";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const SB_URL  = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface Manufacturer {
  id: string;
  name: string;
  contact_name: string | null;
  contact_email: string | null;
  website: string | null;
  created_at: string;
  is_demo?: boolean;
}

interface Subscription {
  tier: string;
  notes: string | null;
}

interface MfrUser {
  user_id: string;
  role: string;
  created_at: string;
}

interface Product {
  id: string;
  name: string;
  category: string | null;
  active: boolean;
  asset_status: string | null;
}

interface SessionStats {
  total: number;
  lastAt: string | null;
}

const TIER_OPTIONS = ["pending", "free", "paid", "premium"] as const;
type Tier = typeof TIER_OPTIONS[number];

const TIER_CLS: Record<string, string> = {
  pending: "border-gray-700/60 bg-gray-900/30 text-gray-400",
  free:    "border-gray-700/60 bg-gray-900/30 text-gray-400",
  paid:    "border-teal-700/60 bg-teal-900/30 text-teal-400",
  premium: "border-amber-700/60 bg-amber-900/30 text-amber-400",
};

// Keyed on ArDisplayStatus, not raw asset_status: the approved path writes
// `ready`, so the old `complete` comparison fell through to the red "failed"
// branch for every published product.
const AR_DOT: Record<string, string> = {
  ready:      "bg-green-500",
  processing: "bg-blue-500 animate-pulse",
  pending:    "bg-yellow-500",
  failed:     "bg-red-500",
};

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

export default function AdminManufacturerDetail() {
  const { id } = useParams<{ id: string }>();
  const { session } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const jwt = session?.access_token ?? "";

  const [mfr,     setMfr]     = useState<Manufacturer | null>(null);
  const [sub,     setSub]      = useState<Subscription | null>(null);
  const [users,   setUsers]    = useState<MfrUser[]>([]);
  const [products,setProducts] = useState<Product[]>([]);
  const [stats,   setStats]    = useState<SessionStats>({ total: 0, lastAt: null });
  const [loading, setLoading]  = useState(true);
  const [error,   setError]    = useState<string | null>(null);

  // Inline edit states
  const [editingName,  setEditingName]  = useState(false);
  const [nameValue,    setNameValue]    = useState("");
  const [savingName,   setSavingName]   = useState(false);

  const [editingNotes, setEditingNotes] = useState(false);
  const [notesValue,   setNotesValue]   = useState("");
  const [savingNotes,  setSavingNotes]  = useState(false);

  const [changingTier, setChangingTier] = useState(false);

  // Invite modal
  const [inviteOpen,   setInviteOpen]   = useState(false);
  const [inviteEmail,  setInviteEmail]  = useState("");
  const [inviteRole,   setInviteRole]   = useState<"viewer" | "admin">("viewer");
  const [inviteLoading,setInviteLoading]= useState(false);
  const [inviteError,  setInviteError]  = useState<string | null>(null);

  /* ── Fetch all data ─────────────────────────────────────────── */
  const load = useCallback(async () => {
    if (!jwt || !id) return;
    setLoading(true);
    setError(null);
    try {
      // Manufacturer
      const mRes = await fetch(
        `${SB_URL}/rest/v1/manufacturers?id=eq.${id}&select=id,name,contact_name,contact_email,website,created_at,is_demo&limit=1`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (!mRes.ok) throw new Error(`Manufacturer fetch: ${mRes.status}`);
      const [mfrRow]: Manufacturer[] = await mRes.json();
      if (!mfrRow) throw new Error("Manufacturer not found");
      setMfr(mfrRow);
      setNameValue(mfrRow.name);

      // Subscription
      const sRes = await fetch(
        `${SB_URL}/rest/v1/manufacturer_subscriptions?manufacturer_id=eq.${id}&select=tier,notes&limit=1`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (sRes.ok) {
        const [subRow]: Subscription[] = await sRes.json();
        if (subRow) { setSub(subRow); setNotesValue(subRow.notes ?? ""); }
      }

      // Users
      const uRes = await fetch(
        `${SB_URL}/rest/v1/manufacturer_users?manufacturer_id=eq.${id}&select=user_id,role,created_at&order=created_at.asc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (uRes.ok) setUsers(await uRes.json());

      // Products
      const pRes = await fetch(
        `${SB_URL}/rest/v1/products?manufacturer_id=eq.${id}&select=id,name,category,active,asset_status&order=name.asc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (pRes.ok) setProducts(await pRes.json());

      // Session events stats — count + last event date
      const evRes = await fetch(
        `${SB_URL}/rest/v1/session_events?manufacturer_id=eq.${id}&select=created_at&order=created_at.desc`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, Prefer: "count=exact" } }
      );
      if (evRes.ok) {
        const evRows: { created_at: string }[] = await evRes.json();
        const contentRange = evRes.headers.get("content-range") ?? "";
        const total = contentRange ? parseInt(contentRange.split("/")[1] ?? "0", 10) : evRows.length;
        setStats({ total: isNaN(total) ? evRows.length : total, lastAt: evRows[0]?.created_at ?? null });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [jwt, id]);

  useEffect(() => { void load(); }, [load]);

  /* ── Save name ──────────────────────────────────────────────── */
  const handleSaveName = async () => {
    if (!jwt || !id || !nameValue.trim()) return;
    setSavingName(true);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/manufacturers?id=eq.${id}`, {
        method: "PATCH",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}`, "Content-Type": "application/json", Prefer: "return=minimal" },
        body: JSON.stringify({ name: nameValue.trim() }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "Name saved" });
      setEditingName(false);
      setMfr((prev) => prev ? { ...prev, name: nameValue.trim() } : prev);
    } catch (e) {
      toast({ title: "Save failed", description: e instanceof Error ? e.message : "", variant: "destructive" });
    } finally {
      setSavingName(false);
    }
  };

  /* ── Change tier ─────────────────────────────────────────────── */
  const handleTierChange = async (tier: Tier) => {
    if (!jwt || !id) return;
    setChangingTier(true);
    try {
      // Upsert subscription row
      const res = await fetch(`${SB_URL}/rest/v1/manufacturer_subscriptions`, {
        method: "POST",
        headers: {
          apikey: ANON_KEY, Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
          Prefer: "resolution=merge-duplicates,return=minimal",
        },
        body: JSON.stringify({ manufacturer_id: id, tier }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: `Tier updated to ${tier}` });
      setSub((prev) => ({ tier, notes: prev?.notes ?? null }));
    } catch (e) {
      toast({ title: "Tier update failed", description: e instanceof Error ? e.message : "", variant: "destructive" });
    } finally {
      setChangingTier(false);
    }
  };

  /* ── Save notes ─────────────────────────────────────────────── */
  const handleSaveNotes = async () => {
    if (!jwt || !id) return;
    setSavingNotes(true);
    try {
      const res = await fetch(`${SB_URL}/rest/v1/manufacturer_subscriptions`, {
        method: "POST",
        headers: {
          apikey: ANON_KEY, Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
          Prefer: "resolution=merge-duplicates,return=minimal",
        },
        body: JSON.stringify({ manufacturer_id: id, notes: notesValue }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "Notes saved" });
      setEditingNotes(false);
      setSub((prev) => prev ? { ...prev, notes: notesValue } : { tier: "pending", notes: notesValue });
    } catch (e) {
      toast({ title: "Save failed", description: e instanceof Error ? e.message : "", variant: "destructive" });
    } finally {
      setSavingNotes(false);
    }
  };

  /* ── Invite user ─────────────────────────────────────────────── */
  const handleInviteSubmit = async () => {
    if (!jwt || !id) return;
    const email = inviteEmail.trim();
    if (!email) { setInviteError("Email is required."); return; }
    setInviteLoading(true);
    setInviteError(null);
    try {
      const res = await fetch(`${SB_URL}/functions/v1/invite-manufacturer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", apikey: ANON_KEY, Authorization: `Bearer ${jwt}` },
        body: JSON.stringify({ email, manufacturer_id: id, role: inviteRole }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as Record<string, string>)?.error ?? `${res.status}`);
      }
      setInviteOpen(false);
      setInviteEmail("");
      toast({ title: "Invite sent", description: `Invitation sent to ${email}` });
      await load();
    } catch (e) {
      setInviteError(e instanceof Error ? e.message : "Failed to send invite.");
    } finally {
      setInviteLoading(false);
    }
  };

  /* ── Remove user ─────────────────────────────────────────────── */
  const handleRemoveUser = async (userId: string) => {
    if (!jwt || !id) return;
    try {
      const res = await fetch(
        `${SB_URL}/rest/v1/manufacturer_users?manufacturer_id=eq.${id}&user_id=eq.${userId}`,
        { method: "DELETE", headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
      );
      if (!res.ok) throw new Error(`${res.status}`);
      toast({ title: "User removed" });
      setUsers((prev) => prev.filter((u) => u.user_id !== userId));
    } catch (e) {
      toast({ title: "Remove failed", description: e instanceof Error ? e.message : "", variant: "destructive" });
    }
  };

  /* ── Render ─────────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-24 rounded-xl border border-gray-800 bg-gray-900 animate-pulse" />)}
      </div>
    );
  }

  if (error || !mfr) {
    return (
      <div className="rounded-xl border border-red-900/40 bg-red-900/20 px-5 py-4 text-sm text-red-400">
        {error ?? "Manufacturer not found."}
        <button onClick={() => navigate("/admin/manufacturers")} className="ml-3 underline hover:text-red-300">
          Back
        </button>
      </div>
    );
  }

  const currentTier = (sub?.tier ?? "pending") as Tier;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate("/admin/manufacturers")} className="text-gray-500 hover:text-gray-300 text-sm transition">
          ← Manufacturers
        </button>
        <span className="text-gray-700">/</span>
        <h1 className="text-xl font-semibold text-gray-100">{mfr.name}</h1>
        {mfr.is_demo && (
          <span className="inline-flex items-center rounded-full border border-amber-700/60 bg-amber-900/40 px-2 py-0.5 text-xs font-medium text-amber-400">
            Demo
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">

          {/* Name */}
          <section className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-200">Manufacturer Name</h2>
              {!editingName && (
                <button onClick={() => setEditingName(true)} className="text-xs text-gray-500 hover:text-gray-300 transition">Edit</button>
              )}
            </div>
            {editingName ? (
              <div className="flex gap-2">
                <Input
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  className="bg-gray-950 border-gray-700 text-gray-100"
                  onKeyDown={(e) => { if (e.key === "Enter") void handleSaveName(); if (e.key === "Escape") setEditingName(false); }}
                />
                <button
                  onClick={() => void handleSaveName()}
                  disabled={savingName || !nameValue.trim()}
                  className="rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-40 transition"
                >
                  {savingName ? "…" : "Save"}
                </button>
                <button onClick={() => { setEditingName(false); setNameValue(mfr.name); }} className="text-xs text-gray-500 hover:text-gray-300 transition">
                  Cancel
                </button>
              </div>
            ) : (
              <p className="text-gray-100 text-base font-medium">{mfr.name}</p>
            )}
            <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-gray-500">
              <div><span className="text-gray-600">Contact</span> <span className="ml-2 text-gray-400">{mfr.contact_name ?? "—"}</span></div>
              <div><span className="text-gray-600">Email</span> <span className="ml-2 text-gray-400">{mfr.contact_email ?? "—"}</span></div>
              <div><span className="text-gray-600">Website</span> <span className="ml-2 text-gray-400">{mfr.website ?? "—"}</span></div>
              <div><span className="text-gray-600">Added</span> <span className="ml-2 text-gray-400">{new Date(mfr.created_at).toLocaleDateString()}</span></div>
            </div>
          </section>

          {/* Users */}
          <section className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-200">Portal Users ({users.length})</h2>
              <button
                onClick={() => { setInviteOpen(true); setInviteEmail(""); setInviteRole("viewer"); setInviteError(null); }}
                className="text-xs rounded-lg border border-gray-700 px-2.5 py-1 text-purple-400 hover:text-purple-300 hover:border-purple-700 transition"
              >
                + Invite
              </button>
            </div>
            {users.length === 0 ? (
              <p className="text-xs text-gray-600">No users linked yet.</p>
            ) : (
              <div className="rounded-lg border border-gray-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900/80 border-b border-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">User ID</th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">Role</th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">Added</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.user_id} className="border-t border-gray-800 hover:bg-gray-900/40 transition">
                        <td className="px-3 py-2 font-mono text-gray-400">{u.user_id.slice(0, 8)}…</td>
                        <td className="px-3 py-2">
                          <span className={`capitalize font-medium ${u.role === "admin" ? "text-orange-400" : "text-gray-400"}`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-gray-500">
                          {new Date(u.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => void handleRemoveUser(u.user_id)}
                            className="text-red-500 hover:text-red-400 transition"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Products */}
          <section className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-200">Products ({products.length})</h2>
              <button
                onClick={() => navigate(`/admin/products?manufacturer=${id}`)}
                className="text-xs text-orange-400 hover:text-orange-300 transition"
              >
                View all →
              </button>
            </div>
            {products.length === 0 ? (
              <p className="text-xs text-gray-600">No products yet.</p>
            ) : (
              <div className="rounded-lg border border-gray-800 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900/80 border-b border-gray-800">
                    <tr>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">Name</th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">Category</th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">AR</th>
                      <th className="px-3 py-2 text-left text-gray-500 font-medium">Status</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {products.slice(0, 20).map((p) => (
                      <tr key={p.id} className="border-t border-gray-800 hover:bg-gray-900/30 transition">
                        <td className="px-3 py-2 text-gray-200 max-w-[180px] truncate">{p.name}</td>
                        <td className="px-3 py-2 text-gray-400">{p.category ?? "—"}</td>
                        <td className="px-3 py-2">
                          {arDisplayStatus(p) && (
                            <span className={`inline-block h-2 w-2 rounded-full ${
                              AR_DOT[arDisplayStatus(p)!] ?? "bg-gray-600"
                            }`} />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`text-xs ${p.active ? "text-green-400" : "text-gray-600"}`}>
                            {p.active ? "Active" : "Draft"}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button onClick={() => navigate(`/admin/products/${p.id}`)} className="text-orange-400 hover:underline">
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {products.length > 20 && (
                  <p className="px-3 py-2 text-xs text-gray-600 border-t border-gray-800">
                    {products.length - 20} more — <button onClick={() => navigate(`/admin/products?manufacturer=${id}`)} className="text-orange-400 hover:underline">view all</button>
                  </p>
                )}
              </div>
            )}
          </section>
        </div>

        {/* Right column */}
        <div className="space-y-6">

          {/* Subscription tier */}
          <section className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
            <h2 className="text-sm font-semibold text-gray-200 mb-3">Subscription Tier</h2>
            <div className="flex items-center gap-3 mb-3">
              <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-medium capitalize ${TIER_CLS[currentTier]}`}>
                {currentTier}
              </span>
            </div>
            <Select value={currentTier} onValueChange={(v) => { void handleTierChange(v as Tier); }} disabled={changingTier}>
              <SelectTrigger className="bg-gray-950 border-gray-700 text-gray-100 text-xs">
                <SelectValue placeholder="Change tier…" />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700 text-gray-100">
                {TIER_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          {/* Session stats */}
          <section className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
            <h2 className="text-sm font-semibold text-gray-200 mb-3">Session Events</h2>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Total events</span>
                <span className="font-semibold text-gray-100">{stats.total.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Last event</span>
                <span className="text-gray-400">
                  {stats.lastAt
                    ? new Date(stats.lastAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
                    : "—"}
                </span>
              </div>
            </div>
          </section>

          {/* Notes */}
          <section className="rounded-xl border border-gray-800 bg-gray-900/60 p-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-200">Notes</h2>
              {!editingNotes && (
                <button onClick={() => setEditingNotes(true)} className="text-xs text-gray-500 hover:text-gray-300 transition">Edit</button>
              )}
            </div>
            {editingNotes ? (
              <div className="space-y-2">
                <textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  rows={4}
                  className="w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 outline-none focus:border-orange-500 transition resize-none"
                  placeholder="Internal notes about this manufacturer…"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => void handleSaveNotes()}
                    disabled={savingNotes}
                    className="rounded-lg bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700 disabled:opacity-40 transition"
                  >
                    {savingNotes ? "Saving…" : "Save"}
                  </button>
                  <button onClick={() => { setEditingNotes(false); setNotesValue(sub?.notes ?? ""); }} className="text-xs text-gray-500 hover:text-gray-300 transition">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-400 whitespace-pre-wrap">
                {sub?.notes?.trim() || <span className="italic text-gray-600">No notes.</span>}
              </p>
            )}
          </section>
        </div>
      </div>

      {/* Invite modal */}
      <Dialog open={inviteOpen} onOpenChange={(open) => { if (!open) setInviteOpen(false); }}>
        <DialogContent className="bg-gray-900 border-gray-800 text-gray-100 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-100">Invite user to {mfr.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-300">Email address</label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="bg-gray-950 border-gray-700 text-gray-100 placeholder-gray-500 focus:border-orange-500"
                onKeyDown={(e) => { if (e.key === "Enter") void handleInviteSubmit(); }}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-300">Role</label>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as "viewer" | "admin")}>
                <SelectTrigger className="bg-gray-950 border-gray-700 text-gray-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700 text-gray-100">
                  <SelectItem value="viewer">Viewer — read-only access</SelectItem>
                  <SelectItem value="admin">Admin — full portal access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {inviteError && (
              <p className="text-sm text-red-400 rounded-md border border-red-900/40 bg-red-900/20 px-3 py-2">
                {inviteError}
              </p>
            )}
          </div>
          <DialogFooter className="gap-2">
            <button
              onClick={() => setInviteOpen(false)}
              className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition"
            >
              Cancel
            </button>
            <button
              onClick={() => void handleInviteSubmit()}
              disabled={inviteLoading}
              className="rounded-lg bg-orange-600 px-4 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 transition"
            >
              {inviteLoading ? "Sending…" : "Send Invite"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
